import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import "./App.css";

// Import components from their individual files
import { PetitionDetail } from './PetitionDetail';
import { StartPetitionSection } from './StartPetitionSection';
import { Footer } from './Footer';
import { AllPetitionsPage } from './AllPetitionsPage';
import { PrivacyPolicy } from './PrivacyPolicy';
import { TermsAndConditions } from './TermsAndConditions';
import { TermManagement } from './TermManagement';

// Import remaining components from main components file
import { 
  getAllPetitions,
  Header, 
  StatsSection, 
  SearchSection, 
  PopularPetitions, 
  CreatePetition,
  OAuthCallback,
  useRobloxAuth,
  hasMaintenanceAccess,
  MaintenanceMode,
  hasCurrentTerm,
  getParliamentaryTerms,
  signPetition,
  unsignPetition
} from './components';

// Main App Content Component
const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPetition, setSelectedPetition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  // ROBLOX Authentication
  const robloxAuth = useRobloxAuth();

  // Check maintenance mode on component mount and set up listener
  useEffect(() => {
    const checkMaintenanceMode = () => {
      const maintenanceStatus = localStorage.getItem('maintenance_mode') === 'true';
      setIsMaintenanceMode(maintenanceStatus);
    };

    // Initial check
    checkMaintenanceMode();

    // Listen for maintenance mode changes
    const handleMaintenanceChange = () => {
      checkMaintenanceMode();
    };

    window.addEventListener('maintenanceModeChanged', handleMaintenanceChange);

    return () => {
      window.removeEventListener('maintenanceModeChanged', handleMaintenanceChange);
    };
  }, []);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Listen for OAuth navigation events
  useEffect(() => {
    const handleNavigateToHome = (event) => {
      console.log('🏠 Navigate to home event received, redirecting via React Router...');
      navigate('/');
    };

    // Add event listener for OAuth completion navigation
    window.addEventListener('navigate-to-home', handleNavigateToHome);

    return () => {
      window.removeEventListener('navigate-to-home', handleNavigateToHome);
    };
  }, [navigate]);

  // Helper functions for navigation
  const handlePetitionClick = (petition) => {
    console.log('handlePetitionClick called with:', petition?.id); // Add debugging
    setSelectedPetition(petition);
    navigate(`/petition/${petition.id}`);
  };

  const handleViewAllPetitions = () => {
    console.log('handleViewAllPetitions called'); // Add debugging
    navigate('/petitions');
  };

  const handlePrivacyClick = () => {
    navigate('/privacypolicy');
  };

  const handleBackToPetitions = () => {
    navigate('/petitions');
  };

  const setCurrentPage = (page) => {
    console.log('setCurrentPage called with:', page); // Add debugging
    switch(page) {
      case 'home':
        navigate('/');
        break;
      case 'petitions':
        navigate('/petitions');
        break;
      case 'create-petition':
        navigate('/create-petition');
        break;
      case 'privacy':
        navigate('/privacypolicy');
        break;
      default:
        navigate('/');
    }
  };

  // HomePage Component
  const HomePage = () => {
    const [parliamentInSession, setParliamentInSession] = useState(false);
    const [termsLoading, setTermsLoading] = useState(true);
    
    // Load parliamentary terms to check if Parliament is in session
    useEffect(() => {
      const loadTerms = async () => {
        setTermsLoading(true);
        try {
          const fetchedTerms = await getParliamentaryTerms();
          const inSession = hasCurrentTerm(fetchedTerms);
          console.log('App.js HomePage: Terms loaded, parliament in session:', inSession);
          setParliamentInSession(inSession);
        } catch (error) {
          console.error('App.js HomePage: Error loading terms:', error);
          setParliamentInSession(false);
        } finally {
          setTermsLoading(false);
        }
      };

      loadTerms();
    }, []);
    
    return (
      <>
        {/* Parliament Session Status Notice */}
        {!termsLoading && !parliamentInSession && (
          <div className="bg-red-50 border-l-4 border-red-500 py-4">
            <div className="container mx-auto px-4">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <div className="text-sm text-red-700">
                    <p className="font-medium">Parliament is not currently in session</p>
                    <p className="mt-1">New petitions are not being accepted at this time. Please check back when the next parliamentary term begins.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <StatsSection />
        <SearchSection 
          searchQuery={searchQuery} 
          setSearchQuery={setSearchQuery} 
          onSearch={() => navigate('/petitions')}
          onViewAllPetitions={handleViewAllPetitions}
        />
        <PopularPetitions 
          onPetitionClick={handlePetitionClick} 
          onViewAllClick={handleViewAllPetitions} 
          setCurrentPage={setCurrentPage}
        />
        <StartPetitionSection 
          user={robloxAuth.user} 
          setCurrentPage={setCurrentPage} 
        />
      </>
    );
  };

  // PetitionDetailPage Component
  const PetitionDetailPage = () => {
    const petitionId = location.pathname.split('/')[2];
    const [petition, setPetition] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Fetch petition from API
    useEffect(() => {
      const fetchPetition = async () => {
        setLoading(true);
        setError(null);
        
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}`);
          
          if (!response.ok) {
            if (response.status === 404) {
              setError('Petition not found');
            } else {
              setError('Failed to load petition');
            }
            setPetition(null);
            return;
          }
          
          const petitionData = await response.json();
          setPetition(petitionData);
        } catch (err) {
          console.error('Error fetching petition:', err);
          setError('Failed to load petition');
          setPetition(null);
        } finally {
          setLoading(false);
        }
      };
      
      if (petitionId) {
        fetchPetition();
      }
    }, [petitionId]);
    
    if (loading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading petition...</p>
          </div>
        </div>
      );
    }
    
    if (error || !petition) {
      return (
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {error === 'Petition not found' ? 'Petition Not Found' : 'Error Loading Petition'}
            </h2>
            <p className="text-gray-600 mb-4">
              {error === 'Petition not found' 
                ? 'The petition you\'re looking for doesn\'t exist or may have been removed.' 
                : 'There was an error loading the petition. Please try again.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Return to Home
            </button>
          </div>
        </div>
      );
    }

    // Handle petition signing
    const handleSignPetition = async (petitionId, shouldSign) => {
      if (!robloxAuth.user) {
        alert('Please log in to sign petitions');
        return;
      }

      try {
        let result;
        if (shouldSign) {
          result = await signPetition(petitionId, robloxAuth.user);
        } else {
          result = await unsignPetition(petitionId, robloxAuth.user);
        }

        if (result.success) {
          // Refresh the petition data
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/petitions/${petitionId}`);
          if (response.ok) {
            const updatedPetition = await response.json();
            setPetition(updatedPetition);
          }
        } else {
          alert(result.error || 'Failed to update signature');
        }
      } catch (error) {
        console.error('Error signing petition:', error);
        alert('Failed to update signature. Please try again.');
      }
    };

    return (
      <PetitionDetail 
        petition={petition} 
        onBack={handleBackToPetitions} 
        user={robloxAuth.user}
        onSign={handleSignPetition}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* OAuth Callback Loading Screen */}
      {robloxAuth.oauthCallbackLoading && (
        <div className="fixed inset-0 bg-white z-50 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto"></div>
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Completing Sign In</h1>
            <p className="text-gray-600 mb-4">
              Please wait while we complete your authentication with ROBLOX...
            </p>
            <p className="text-gray-500 text-sm mb-2">
              Verifying your identity and loading your profile.
            </p>
            <p className="text-gray-400 text-xs">
              Please do not close this window.
            </p>
          </div>
        </div>
      )}
      
      {/* Only show maintenance mode if:
          1. Maintenance is enabled AND
          2. User is loaded (not null/undefined) AND  
          3. User doesn't have maintenance access
          This prevents blocking health checks and deployment */}
      {isMaintenanceMode && robloxAuth.user && !hasMaintenanceAccess(robloxAuth.user) ? (
        <MaintenanceMode />
      ) : (
        <>
          <Header 
            currentPage={location.pathname} 
            setCurrentPage={setCurrentPage} 
            user={robloxAuth.user} 
            robloxAuth={robloxAuth} 
          />
          
          <main className="flex-1">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/petitions" element={
                <AllPetitionsPage onPetitionClick={handlePetitionClick} />
              } />
              <Route path="/petition/:id" element={<PetitionDetailPage />} />
              <Route path="/create-petition" element={
                <CreatePetition 
                  user={robloxAuth.user} 
                  setCurrentPage={setCurrentPage} 
                  loading={robloxAuth.loading}
                />
              } />
              <Route path="/privacypolicy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/term-management" element={
                <TermManagement 
                  user={robloxAuth.user} 
                  onBack={() => navigate('/')}
                />
              } />
              <Route path="/auth/callback" element={
                <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-emerald-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-900">Processing Authentication...</h2>
                    <p className="text-gray-600 mt-2">Please wait while we complete your sign in.</p>
                  </div>
                </div>
              } />
              <Route path="*" element={
                <div className="container mx-auto px-4 py-8">
                  <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Page Not Found</h2>
                    <p className="text-gray-600 mb-4">The page you're looking for doesn't exist.</p>
                    <button
                      onClick={() => navigate('/')}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Return to Home
                    </button>
                  </div>
                </div>
              } />
            </Routes>
          </main>
          
          <Footer 
            setCurrentPage={setCurrentPage} 
          />
        </>
      )}
    </div>
  );
};

function App() {
  useEffect(() => {
    document.title = 'Petitions - UK Government and Parliament';
  }, []);

  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
